import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaSearch, 
  FaFilter, 
  FaEye, 
  FaSpinner, 
  FaExclamationTriangle, 
  FaClock, 
  FaCheck,
  FaEnvelope,
  FaTimes
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getAllQuotations, updateQuotationStatus } from '../../services/quotationService';

// Colores para los estados de las cotizaciones
const STATUS_COLORS = {
  pending: 'warning',
  processing: 'info',        // Cambiado de estimado_listo a processing
  completed: 'success',      // Cambiado de completada a completed
  rejected: 'danger'         // Cambiado de rechazada a rejected
};

// Etiquetas para los estados de las cotizaciones
const STATUS_LABELS = {
  pending: 'Pendiente',
  processing: 'En Proceso',  // Cambiado de estimado_listo a processing
  completed: 'Completada',   // Cambiado de completada a completed
  rejected: 'Rechazada'      // Cambiado de rechazada a rejected
};

const Quotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updating, setUpdating] = useState({});
  const [updateSuccess, setUpdateSuccess] = useState(null);

  useEffect(() => {
    fetchQuotations();
  }, []);

  // Obtener todas las cotizaciones reales de Back4App
  const fetchQuotations = async () => {
    setLoading(true);
    try {
      // Obtener cotizaciones reales desde Back4App
      const quotationsData = await getAllQuotations();
      
      // Mapear los datos de Parse a un formato más fácil de usar
      const mappedQuotations = quotationsData.map(quotation => ({
        id: quotation.id,
        createdAt: quotation.get('createdAt'),
        userEmail: quotation.get('userEmail'),
        userName: quotation.get('userName'),
        userPhone: quotation.get('userPhone'),
        userCompany: quotation.get('userCompany'),
        magicLink: quotation.get('magicLink'),
        status: quotation.get('status') || 'pending',
        items: quotation.get('items') || [],
        message: quotation.get('message'),
        additionalNotes: quotation.get('additionalNotes'),
        converted: quotation.get('converted') || false,
        user: quotation.get('user')
      }));

      setQuotations(mappedQuotations);
      setFilteredQuotations(mappedQuotations);
      setError(null);
    } catch (err) {
      console.error('Error al cargar cotizaciones:', err);
      setError('No se pudieron cargar las cotizaciones. Verifica tu conexión e inténtalo de nuevo.');
      toast.error('Error al cargar cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar cotizaciones por estado y término de búsqueda
  useEffect(() => {
    let filtered = quotations;

    // Filtrar por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter(quotation => 
        quotation.status === statusFilter
      );
    }

    // Filtrar por término de búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(quotation => {
        const email = quotation.userEmail?.toLowerCase() || '';
        const magicLink = quotation.magicLink?.toLowerCase() || '';
        const userName = quotation.userName?.toLowerCase() || '';
        
        // Buscar en productos
        const items = quotation.items || [];
        const hasMatchingProduct = items.some(item => 
          item.name?.toLowerCase().includes(term)
        );
        
        return email.includes(term) || 
               magicLink.includes(term) || 
               userName.includes(term) ||
               hasMatchingProduct;
      });
    }

    setFilteredQuotations(filtered);
  }, [quotations, statusFilter, searchTerm]);

  // Actualizar el estado de una cotización usando Back4App
  const handleStatusUpdate = async (quotationId, newStatus) => {
    const quotation = quotations.find(q => q.id === quotationId);
    if (quotation.status === newStatus) return;
    
    // Marcar como actualizando
    setUpdating(prev => ({ ...prev, [quotationId]: true }));
    
    try {
      // Llamar al servicio real para actualizar el estado
      await updateQuotationStatus(quotationId, newStatus);
      
      // Actualizar la lista de cotizaciones
      const updatedQuotations = quotations.map(q => {
        if (q.id === quotationId) {
          return { ...q, status: newStatus };
        }
        return q;
      });
      
      setQuotations(updatedQuotations);
      setFilteredQuotations(updatedQuotations.filter(q => 
        statusFilter === 'all' || q.status === statusFilter
      ).filter(quotation => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        const email = quotation.userEmail?.toLowerCase() || '';
        const userName = quotation.userName?.toLowerCase() || '';
        const magicLink = quotation.magicLink?.toLowerCase() || '';
        
        // Buscar en productos
        const items = quotation.items || [];
        const hasMatchingProduct = items.some(item => 
          item.name?.toLowerCase().includes(term)
        );
        
        return email.includes(term) || 
               magicLink.includes(term) || 
               userName.includes(term) ||
               hasMatchingProduct;
      }));
      
      // Mostrar mensaje de éxito
      setUpdateSuccess({
        id: quotationId,
        message: `Cotización #${quotationId.substring(0, 8)}... actualizada a "${STATUS_LABELS[newStatus]}".`
      });
      toast.success(`Cotización actualizada a "${STATUS_LABELS[newStatus]}"`)
      
      // No usamos timeouts para ocultar mensajes automáticamente
    } catch (err) {
      console.error('Error updating quotation:', err);
      setError(`No se pudo actualizar la cotización: ${err.message}`);
      toast.error('Error al actualizar la cotización');
      
      // Ocultar mensaje de error después de 3 segundos
      setTimeout(() => {
        setError(null);
      }, 3000);
    } finally {
      // Desmarcar como actualizando
      setUpdating(prev => ({ ...prev, [quotationId]: false }));
    }
  };

  // Formatear fecha
  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  // Renderizar icono de estado
  const renderStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FaClock className="text-warning" />;
      case 'processing':  
        return <FaExclamationTriangle className="text-info" />;
      case 'completed':   
        return <FaCheck className="text-success" />;
      case 'rejected':    
        return <FaTimes className="text-danger" />;
      default:
        return <FaClock className="text-warning" />;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <p>Cargando cotizaciones...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-4">Gestión de Cotizaciones</h2>
      
      {/* Mensajes de éxito o error */}
      {updateSuccess && (
        <div className="alert alert-success" role="alert">
          <FaCheck className="me-2" />
          {updateSuccess.message}
        </div>
      )}
      
      {error && (
        <div className="alert alert-danger" role="alert">
          <FaExclamationTriangle className="me-2" />
          {error}
        </div>
      )}
      
      {/* Filtros y búsqueda */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <FaSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por email, magic link o producto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text">
                  <FaFilter />
                </span>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Todos los estados</option>
                  <option value="pending">Pendientes</option>
                  <option value="estimado_listo">Estimado Listo</option>
                  <option value="completada">Completadas</option>
                  <option value="rechazada">Rechazadas</option>
                </select>
              </div>
            </div>
            <div className="col-md-2">
              <button 
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabla de cotizaciones */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th style={{ width: '12%' }}>Fecha</th>
                  <th style={{ width: '20%' }}>Email</th>
                  <th style={{ width: '20%' }}>Productos</th>
                  <th style={{ width: '13%' }}>Estado</th>
                  <th style={{ width: '20%' }}>Magic Link</th>
                  <th style={{ width: '15%' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotations.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No se encontraron cotizaciones
                    </td>
                  </tr>
                ) : (
                  filteredQuotations.map(quotation => (
                    <tr key={quotation.id}>
                      <td>{formatDate(quotation.createdAt)}</td>
                      <td>{quotation.userEmail}</td>
                      <td>
                        <small>
                          {(quotation.items || []).map(item => item.name).join(', ')}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {renderStatusIcon(quotation.status)}
                          <span className="ms-2">
                            {STATUS_LABELS[quotation.status || 'pending']}
                          </span>
                        </div>
                      </td>
                      <td>
                        <code className="small" style={{ wordBreak: 'break-all', fontSize: '0.75rem', display: 'block' }}>
                          {quotation.magicLink}
                        </code>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Link
                            to={`/admin/quotation/${quotation.id}`}
                            className="btn btn-sm btn-light"
                            title="Ver detalles"
                          >
                            <FaEye />
                          </Link>
                          
                          {/* Botones de cambio de estado */}
                          <div className="dropdown">
                            <button 
                              className="btn btn-sm btn-outline-primary dropdown-toggle" 
                              type="button" 
                              data-bs-toggle="dropdown"
                              disabled={updating[quotation.id]}
                            >
                              {updating[quotation.id] ? (
                                <>Actualizando</>
                              ) : (
                                <>Cambiar estado</>
                              )}
                            </button>
                            <ul className="dropdown-menu">
                              <li>
                                <button 
                                  className="dropdown-item d-flex align-items-center"
                                  onClick={() => handleStatusUpdate(quotation.id, 'pending')}
                                  disabled={quotation.status === 'pending'}
                                >
                                  <FaClock className="text-warning me-2" />
                                  Pendiente
                                </button>
                              </li>
                              <li>
                                <button 
                                  className="dropdown-item d-flex align-items-center"
                                  onClick={() => handleStatusUpdate(quotation.id, 'processing')}
                                  disabled={quotation.status === 'processing'}
                                >
                                  <FaExclamationTriangle className="text-info me-2" />
                                  En Proceso
                                </button>
                              </li>
                              <li>
                                <button 
                                  className="dropdown-item d-flex align-items-center"
                                  onClick={() => handleStatusUpdate(quotation.id, 'completed')}
                                  disabled={quotation.status === 'completed'}
                                >
                                  <FaCheck className="text-success me-2" />
                                  Completada
                                </button>
                              </li>
                              <li>
                                <button 
                                  className="dropdown-item d-flex align-items-center"
                                  onClick={() => handleStatusUpdate(quotation.id, 'rejected')}
                                  disabled={quotation.status === 'rejected'}
                                >
                                  <FaTimes className="text-danger me-2" />
                                  Rechazada
                                </button>
                              </li>
                              <li><hr className="dropdown-divider" /></li>
                              <li>
                                <a 
                                  className="dropdown-item d-flex align-items-center"
                                  href={`mailto:${quotation.userEmail}?subject=Actualización%20de%20cotización%20${quotation.magicLink}`}
                                >
                                  <FaEnvelope className="text-secondary me-2" />
                                  Contactar cliente
                                </a>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          <div className="mt-3">
            <small className="text-muted">
              Mostrando {filteredQuotations.length} de {quotations.length} cotizaciones
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quotations;
